import { api } from './api';
import { useState } from 'react';

export interface Company {
    id: string;
    name: string;
    country: string;
    slug: string;
    logoUrl: string;
    created_at: string;
    updated_at: string;
}

export interface CreateCompanyInput {
    name: string;
    slug: string;
    country: string;
    logo: File;
}

export const companies = api.injectEndpoints({
    endpoints: (builder) => ({
        getCompanies: builder.query<Company[], void>({
            query: () => 'companies',
            transformResponse: (response: any) =>
                response && response.length ? response : [],
            providesTags: ['Company'],
        }),

        getCompany: builder.query<Company, string>({
            query: (companyIdentifier) => `companies/${companyIdentifier}`,
            transformResponse: (response: any) => response || {},
            providesTags: (result, error, id) => [{ type: 'Company', id }],
        }),

        getUploadPresignedUrl: builder.mutation<
            { id: string; url: string },
            { type: string; file_name: string; params: any }
        >({
            query: (body) => ({
                url: 'files/upload',
                method: 'POST',
                body,
            }),
        }),

        confirmUpload: builder.mutation<any, string>({
            query: (fileId) => ({
                url: `files/${fileId}`,
                method: 'PATCH',
            }),
        }),

        createCompany: builder.mutation<
            Company,
            { name: string; country: string; slug: string; logoUrl: string }
        >({
            query: (body) => ({
                url: 'companies',
                method: 'POST',
                body,
            }),
            invalidatesTags: ['Company'],
        }),
    }),
});

export const {
    useGetCompaniesQuery,
    useGetCompanyQuery,
    useLazyGetCompanyQuery,
    useGetUploadPresignedUrlMutation,
    useConfirmUploadMutation,
    useCreateCompanyMutation,
    util: { getRunningQueriesThunk },
} = companies;

// Export the API slice for use in SSR (if needed)
export const { getCompanies, getCompany } = companies.endpoints;

export function useS3Upload() {
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<Error | null>(null);

    const uploadToS3 = async (
        url: string,
        file: File,
        contentType?: string,
    ): Promise<boolean> => {
        setIsLoading(true);
        setError(null);

        try {
            const response = await fetch(url, {
                method: 'PUT',
                body: file,
                headers: {
                    'Content-Type':
                        contentType ??
                        (file.type || 'application/octet-stream'),
                },
            });

            if (!response.ok) {
                setError(
                    new Error(
                        `Upload failed: ${response.status} ${response.statusText}`,
                    ),
                );
            }

            return true;
        } catch (err) {
            setError(
                err instanceof Error
                    ? err
                    : new Error('Unknown error during upload'),
            );
            return false;
        } finally {
            setIsLoading(false);
        }
    };

    return { uploadToS3, isLoading, error };
}
